import { Application, Assets, Sprite, Texture, Container, TilingSprite } from 'pixi.js';
import { toast } from '../../dom';
import { handleSessionExpiry } from '../../session-expiry';
import { fieldApi, type Farm, type Crop, type WoodStatus, type MineStatus, type GatherStatus, type GatherKind } from '../../game-api';
import { ApiError } from '../../../core/network/api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import type { GameCtx } from '../../types';
import { TILE, claimedTiles, pruneReservedDecor, reservedTiles, type World, type Decor } from '../../map/world';
import { NATIVE_PX, TREE_KINDS, type MapTextures } from '../../map/textures';
import { oreByVariant, oreNodeSprite, tierTintNum } from '../../map/ores';
import type { Camera } from '../../map/camera';
import { FOOT_OFFSET, type ActionType, type Dir, type PlayerHandle } from '../../map/player';
import type { Joystick } from '../../map/joystick';
import type { DayNight, ClockConfig } from '../../map/daynight';
import type { HeldTorch } from '../../map/lights';
import { DEFAULT_TUNING, DEFAULT_STATS, type Fauna, type SpeciesStat, type FaunaTuning } from '../../map/fauna';
import { type Villagers } from '../../map/villagers';
import type { Drops, GroundDropData } from '../../map/drops';
import { DEFAULT_MOB_TUNING, type Mobs } from '../../map/mobs';
import type { Appearance } from '../../character/compositor';
import { loadSettings } from '../../settings-modal';
import { playSfx } from '../../audio/audio';

// "Tall" decor occludes the player and can fade when walked behind; it lives in
// the culled, y-sorted entity layer. Small ground decor stays in the cached
// scenery group (the player just walks over it). Gatherable small props (rocks,
// mushrooms) also live in the entity layer — they need per-sprite events and
// hide/respawn, which the cached scenery group can't do.
export const TALL_KINDS = new Set<Decor['kind']>(['tree', 'hillock', 'mountain', 'boulder', 'ore_rock', 'rock', 'mushrooms', 'bush', 'dungeon',
  // structures + tall ground decor y-sort with the hero (occlude / be occluded)
  'well', 'signpost', 'scarecrow', 'fence', 'barrel', 'lantern', 'logs', 'flowerpot', 'pebbles',
  // A hay bale is chest-high — it was the one sizeable prop left in the flat
  // scatter layer, which meant the hero and every tree drew OVER it no matter
  // where they stood, and it never occluded anything itself.
  'haybale',
  'basalt_pillar', 'basalt_boulder', 'volcano_tower', 'fountain', 'oracle']);
// §4: bushes join rocks/mushrooms as gatherable props — chopped for sticks (wood),
// tiny collider, they clear the tile on removal so you can till/build there.
/**
 * Props worked with a hold-to-gather ring. This set is what makes a decor sprite
 * INTERACTIVE at all (scene.ts branches on it): a kind missing from here is scenery
 * the pointer walks straight past.
 *
 * `logs` was missing, which is the whole of the "tree leg I can't destroy" report —
 * world-gen scatters woodpiles on the grass and nothing could ever touch one.
 */
export const GATHER_KINDS = new Set<Decor['kind']>(['rock', 'mushrooms', 'bush', 'boulder', 'logs']);

/**
 * A big rock does not come apart in one pass. It takes {@link BOULDER_PASSES}
 * mining rounds, each one paying out stone and visibly chipping the sprite down,
 * before the outcrop is gone and its tile is walkable again.
 */
// 3, not 4: each pass bills as one stone gather (1 stone), and the owner's rule is
// that a big rock yields 3. See STONE_MIN/STONE_MAX in backend gathering.config.ts.
export const BOULDER_PASSES = 3;
/** Which gather endpoint clears this prop. Ground cover (tufts, wildflowers) is
 *  clearable too, so a player can tidy the ground around a bed. */
export const gatherResource = (d: Decor): GatherKind =>
  (d.kind === 'rock' || d.kind === 'boulder' ? 'stone'
    : d.kind === 'bush' ? 'bush'
    : d.kind === 'grass_tuft' ? 'grass'
    : d.kind === 'flowers' ? 'flowers'
    // A felled trunk lying on the grass. It was scenery — in no gatherable list at
    // all — so clicking it did nothing, and since a bush is often drawn in front of
    // one, clearing the bush is when the player meets it. Owner: "from the ground
    // appears tree leg, which i can't destroy."
    : d.kind === 'logs' ? 'logs'
    : 'mushroom');

/** Props cleared by hand in one tap — no hold-to-gather ring, no tool needed. */
export const FOLIAGE_KINDS = new Set<Decor['kind']>(['grass_tuft', 'flowers']);
export const CULL_MARGIN = 6;
export const RUN_TICK_S = 5;        // one energy per this many seconds of running (field.config ENERGY_DRAIN.run)          // tiles of off-screen margin kept instantiated
export const FADE_ALPHA = 0.55;        // opacity of a tall object the player is behind

// Every map sprite is 16px-native pack art, so one uniform scale sizes them all
// pixel-correctly: 1 native pixel → TILE/16 screen pixels. A flower (~10px) lands
// at ~0.6 tile; a big tree (~63px) at ~4 tiles.
export const PX = TILE / NATIVE_PX;

export const TREE_GROW_MS = 24_000;                      // a regrown sapling grows to full size over this
export const CUT_SECONDS = 2.2;    // hold this long to fell a tree
export const MINE_SECONDS = 2.6;   // hold this long to break an ore rock
export const GATHER_SECONDS = 1.8; // small stones take a short mining pass too (mushrooms stay instant)
export const BUCKET = 8;                                // tiles per spatial bucket
export const bkey = (tx: number, ty: number) => `${Math.floor(tx / BUCKET)},${Math.floor(ty / BUCKET)}`;
export const AXE_CURSOR = 'url(/sprites/ui/axe_cursor.png) 8 8, pointer';
export const PICK_CURSOR = 'url(/sprites/ui/pickaxe_cursor.png) 8 8, pointer';
export const HOE_CURSOR = 'url(/sprites/ui/hoe_cursor.png) 8 8, pointer';
export const PATH_CACHE_MAX = 64;

export type AnimWater = { sprite: Sprite; frames: Texture[]; fps: number; t: number; i: number };
export type Particle = { s: Container; vx: number; vy: number; life: number; max: number; spin: number };
export type Cutting = { id: string; d: Decor; sprite: Sprite; t: number; ring: Sprite; chipT: number; dur: number; stump: boolean; rock: boolean; gather: boolean };
/**
 * Worn/held tier levels mirrored from the crafting status. Armor tiers drive the
 * paper-doll layers; the tool + weapon tiers drive swing damage and the held-item
 * sprite, so a Mythril sword actually hits harder than the wooden one you start
 * with (the server applies the same ladder — see `gear.ts weaponDamage`).
 */
export type GearTiers = {
  helmet: number; chest: number; legs: number;
  sword: number; axe: number; pickaxe: number; hoe: number;
};
export type Bag = Awaited<ReturnType<typeof fieldApi.bag>>;

/** Shared mutable state of a live farm view. Every farm module reads/writes it. */
export interface FarmCtx {
  game: GameCtx;
  alive: boolean;
  seed: string;

  farm: Farm | null;
  crops: Crop[];
  fetchedAt: number;

  app: Application | null;
  tex: MapTextures | null;
  world: World | null;
  worldForMax: number;              // maxPlots the current world was generated for
  cam: Camera | null;
  waterLayer: TilingSprite | null;  // full-world animated sea, below the land
  scenery: Container | null;        // static terrain + small decor, cached as a GPU render group
  plotLayer: Container | null;      // dynamic plot cells + crops, y-sorted
  plotSprites: Container[];         // dynamic plot/crop/debug sprites, rebuilt each paint
  entityLayer: Container | null;    // y-sorted, viewport-culled: tall decor + player
  waterFxLayer: Container | null;   // shore water life above the sea, below land
  animWater: AnimWater[];
  player: PlayerHandle | null;
  fxLayer: Container | null;        // chop particles + axe hint + progress ring, on top
  selector: Sprite | null;          // white square bracket shown on the hovered tree
  /** Fishing status (rod ownership + cast cost), fetched with the other configs. */
  fishCfg: import('../../game-api').FishStatus | null;
  /** Bought companions (angel/demon) and the live entities following the hero. */
  companionCfg: import('../../game-api').CompanionStatus | null;
  companions: import('../../map/companion').Companion[];
  /** Rebuild the companion entities after a purchase. Set by the farm view. */
  respawnCompanions?: () => void;
  /** The "next rest charge in…" label under the energy circles, ticked per frame. */
  restTimerLabel: HTMLElement | null;
  /**
   * When energyStatus was fetched, so its countdown can be interpolated.
   *
   * Same reason `fetchedAt` exists for the farm snapshot: every timer on screen is
   * "remaining at fetch − elapsed since", and without the stamp the label would
   * count down from a stale number after any pause.
   */
  energyStatusAt: number | null;
  tileFrame: Sprite | null;         // §3: UI-pack corner bracket on the hovered tile (tint white/green/red)
  treeCursorOn: boolean;            // whether WE set the axe cursor (don't clobber others)
  cropTex: Map<string, Texture[]>;  // plantable id → 4 growth-stage textures
  gridOn: boolean;                  // debug: show each cell + its affected zone
  woodCfg: WoodStatus | null;       // regrow ms etc. (from backend)
  felledUntil: Map<string, number>; // decor.id → regrow timestamp (felled if > now)
  treeGrowStart: Map<string, number>; // decor.id → growth start ts (regrown tree stages sapling→full)

  particles: Particle[];
  pendingAction: { d: Decor; s: Sprite } | null;   // walk-to-object then operate
  pendingPlot: { tx: number; ty: number; tool?: 'hoe'; intent?: 'auto' | 'clear' } | null;   // walk-to-plot-cell then act
  /**
   * Tiles a painted hoe run still owes, in stroke order.
   *
   * Tilling is not build mode: it costs energy and only reaches ONE tile around the
   * hero, so a dragged run of beds cannot fire at once the way a road run does — the
   * hero has to walk the line. The queue is drained one tile at a time by the farm
   * tick (pumpTillQueue), which is also what keeps a 20-bed run to one server call
   * per bed instead of twenty at once.
   */
  tillQueue: { tx: number; ty: number }[];
  pendingTalk: Decor | null;        // walk-to-oracle then open its errand dialog
  pendingMount: boolean;            // walk-to-horse then climb on
  /**
   * The hero was riding when this view was last torn down, so put him back in the
   * saddle once he exists. Set by buildScene from the remembered horse state and
   * consumed on the first frame — see map/horse-state.ts for why a tab switch was
   * dismounting people mid-ride.
   */
  remountPending: boolean;
  buildMode: string | null;         // Block 8: selected building kind to place (null = off)
  /**
   * A placement in progress is RELOCATING this already-placed building rather than
   * creating a new one. Set alongside `buildMode` (which carries the kind, so the
   * ghost and the tile frame need no special case); the commit routes to
   * `field/move` instead of `field/build`, and the building's own tiles are exempt
   * from the "occupied" test — otherwise nothing could ever move by one tile.
   */
  moving: { tx: number; ty: number; kind: string } | null;
  /** Follow-the-cursor preview of the armed building (see build-ghost.ts). */
  buildGhost: Container | null;
  /**
   * Villagers currently living on the farm, or null until the Village panel/build
   * sheet has asked. Population gates the Hamlet-tier buildings server-side; the
   * client mirrors it so a gated kind reads as locked BEFORE the round trip.
   */
  population: number | null;
  cutting: Cutting | null;
  mineCfg: MineStatus | null;            // ore-rock feel constants (from backend)
  minedUntil: Map<string, number>;       // ore_rock decor.id → regrow timestamp (mined out if > now)
  gatherCfg: GatherStatus | null;        // stone/mushroom feel constants (from backend)
  gatheredUntil: Map<string, number>;    // rock/mushrooms decor.id → respawn timestamp
  boulderHits: Map<string, number>;      // boulder decor.id → mining passes landed so far
  clearedStumps: Set<string>;            // stumps grubbed out — hidden until the tree regrows
  itemTex: Map<string, Texture>;
  tallBuckets: Map<string, Decor[]>;     // tall decor bucketed by coarse grid, for culling
  activeTall: Map<string, Sprite>;       // decor.id → live sprite currently in view
  // Small scatter (grass tufts, flowers) is windowed like tall decor: bucketed data,
  // only the in-view sprites live. `activeSmall` also carries the Decor so plots can
  // hide the scatter under them.
  smallBuckets: Map<string, Decor[]>;
  activeSmall: Map<string, { s: Sprite; d: Decor }>;
  smallLayer: Container | null;
  // Terrain + coastline are built PER CHUNK on demand (viewport-windowed) rather than
  // the whole 683×464 map at once. Each active chunk owns its ground render group, its
  // shore sprites, and its shore-animation entries (a slice of animWater).
  terrainChunks: Map<string, { ground: Container; shore: Container; anim: AnimWater[] }>;
  keys: Set<string>;                     // held movement keys
  blockedGrid: Uint8Array | null;        // 1 = impassable (water + landmark footprints)
  gridVersion: number;                   // bumped whenever the collision layout changes
  // The destination the hero is currently walking to, and the layout that route
  // was planned against. If the layout moves on, the route is re-planned rather
  // than walked blindly into whatever just appeared.
  walkGoal: { tx: number; ty: number; v: number } | null;
  draftData: { data: Uint8ClampedArray; w: number; h: number } | null | undefined; // undefined=untried
  pathCache: Map<string, { x: number; y: number }[] | null>;

  /**
   * True while the tutorial's phase-1 controls glade is on screen.
   *
   * The glade is a hand-built 20x50 map with its own tree and rock, drawn over
   * the SAME farm the real world uses. Its props were nevertheless chopping and
   * mining through the real API, at glade tile coordinates — so the training rock
   * at (7,10) wrote a permanent `ore_rock` harvest row on the player's farm.
   * Two things followed: the real world's tile (7,10) was retired before the
   * player ever saw it, and re-entering the glade (a reload mid-tutorial, or a
   * replayed tutorial) hit that row and answered every click on the training rock
   * with `400 That vein is worked out - find another`, which is where the
   * tutorial stopped. Producers check this and keep glade actions client-side.
   */
  sandbox: boolean;

  spawningPlayer: boolean;
  baseLook: Appearance | null;
  gearTiers: GearTiers;

  runMeter: number;
  outOfPuff: boolean;
  joystick: Joystick | null;
  actionBtn: HTMLElement | null;   // mobile context-action button
  dayNight: DayNight | null;
  fog: import('../../map/fog').Fog | null;   // tile-based fog of war (farm only)
  minimap: import('./minimap').Minimap | null;   // Block 5: explored-terrain overview
  effects: import('./effects').Effects | null;   // §7: arrows / slashes / hit sparks
  fauna: Fauna | null;
  /**
   * Owned pen stock — a SECOND Fauna instance (see farm/livestock.ts). Kept apart
   * from `fauna` because clicks and sword swings resolve against that one only:
   * your own cow must not be killable for wild loot.
   */
  livestock: Fauna | null;
  faunaStats: Record<string, SpeciesStat>;
  faunaTuning: FaunaTuning;
  villagers: Villagers | null;
  drops: Drops | null;
  mobs: Mobs | null;
  mobStats: import('../../game-api').MobStatDto[];
  mobTuning: import('../../map/mobs').MobTuning;
  mobSpawnRate: number;
  mobSpawnAccMs: number;         // night spawn timer accumulator
  riding: import('../../map/riding').Riding | null;
  /**
   * The player's tent and its safe radius, as the SERVER last reported them.
   *
   * Cached on the context rather than fetched per frame: the mob loop asks "is the
   * hero safe?" every tick, and that question has to be answered from memory. The
   * server stays authoritative — every tent call returns a fresh status and replaces
   * this — so the client never invents a safe zone the backend does not agree with.
   */
  tent: import('./building-menu').TentStatus | null;
  tentSprite: import('pixi.js').Sprite | null;   // the pitched tent on the map
  tentRing: import('pixi.js').Graphics | null;   // the safe ground it keeps, drawn under it
  horseStatus: import('../../game-api').HorseStatus | null;
  campfire: import('../../map/campfire').CampfireHandle | null;   // the built campfire prop, if any
  onMobHit?: (damage: number) => void;
  onMobDeath?: (m: import('../../map/mobs').MobEntity) => void;
  onMobCallAdds?: (m: import('../../map/mobs').MobEntity, n: number) => void;
  /** Food poisoning just started — the farm view arms its poison countdown. */
  onPoison?: (p: import('../../game-api').PoisonDto) => void;
  energyStatus: import('../../game-api').EnergyStatus | null;
  /** Producers call this with the drops the server just spawned, so they render
   *  immediately without waiting for the next /drops poll. */
  onDrops?: (list: GroundDropData[]) => void;
  clockCfg: ClockConfig | null;

  selectedTool: string | null;
  /** The torch in the hero's hand: animated overlay + a light pool that follows him.
   *  Separate from `selectedTool` because it is carried, not swung — see lights.ts. */
  heldTorch: HeldTorch | null;
  torchLit: boolean;
  /** When the sword (knife) is equipped, auto-swing at the nearest mob/animal in
   *  range on a cadence (field only — the Arena is always manual click). */
  autoAttack: boolean;
  combatTargetId: string | null;    // clicked a mob → keep attacking it until it dies / leaves reach
  bagCache: Bag | null;
  overlay: HTMLElement | null;
  /** Enter a dungeon (Block 6) — set by the farm view; called on entrance click. */
  onEnterDungeon?: (seed: number) => void;
  onTalkOracle?: (d: Decor) => void;   // open the birch-grove oracle's errand dialog

  // DOM shell
  wrap: HTMLElement;
  stageHost: HTMLElement;
  energyBar: HTMLElement;
  footer: HTMLElement;
  hotbar: HTMLElement;
  clockLabel: HTMLElement | null;   // live game-time readout under the energy bar

  /** Re-fetch farm + crops, repaint chrome, (re)build or repaint the world. Wired in renderFarm. */
  load: () => Promise<void>;
}

export function createFarmCtx(
  game: GameCtx,
  dom: { wrap: HTMLElement; stageHost: HTMLElement; energyBar: HTMLElement; footer: HTMLElement; hotbar: HTMLElement },
): FarmCtx {
  return {
    game,
    alive: true,
    seed: game.user.email || game.user.username || 'terraboma',
    farm: null,
    crops: [],
    fetchedAt: 0,
    app: null,
    tex: null,
    world: null,
    worldForMax: -1,
    cam: null,
    waterLayer: null,
    scenery: null,
    plotLayer: null,
    plotSprites: [],
    entityLayer: null,
    waterFxLayer: null,
    animWater: [],
    player: null,
    fxLayer: null,
    selector: null,
    fishCfg: null,
    companionCfg: null,
    companions: [],
    restTimerLabel: null,
    energyStatusAt: null,
    tileFrame: null,
    treeCursorOn: false,
    cropTex: new Map(),
    gridOn: false,
    woodCfg: null,
    felledUntil: new Map(),
    treeGrowStart: new Map(),
    particles: [],
    pendingAction: null,
    pendingPlot: null,
    tillQueue: [],
    pendingTalk: null,
    pendingMount: false,
    remountPending: false,
    buildMode: null,
    moving: null,
    buildGhost: null,
    population: null,
    cutting: null,
    mineCfg: null,
    minedUntil: new Map(),
    gatherCfg: null,
    gatheredUntil: new Map(),
    boulderHits: new Map(),
    clearedStumps: new Set(),
    itemTex: new Map(),
    tallBuckets: new Map(),
    activeTall: new Map(),
    smallBuckets: new Map(),
    activeSmall: new Map(),
    smallLayer: null,
    terrainChunks: new Map(),
    keys: new Set(),
    blockedGrid: null,
    gridVersion: 0,
    walkGoal: null,
    draftData: undefined,
    pathCache: new Map(),
    sandbox: false,
    spawningPlayer: false,
    baseLook: null,
    gearTiers: { helmet: 0, chest: 0, legs: 0, sword: 1, axe: 1, pickaxe: 1, hoe: 1 },
    runMeter: 0,
    outOfPuff: false,
    joystick: null,
    actionBtn: null,
    dayNight: null,
    fog: null,
    minimap: null,
    effects: null,
    fauna: null,
    livestock: null,
    faunaStats: DEFAULT_STATS,
    faunaTuning: DEFAULT_TUNING,
    villagers: null,
    drops: null,
    mobs: null,
    mobStats: [],
    mobTuning: DEFAULT_MOB_TUNING,
    mobSpawnRate: 3,
    mobSpawnAccMs: 0,
    riding: null,
    tent: null,
    tentSprite: null,
    tentRing: null,
    horseStatus: null,
    campfire: null,
    energyStatus: null,
    clockCfg: null,
    selectedTool: null,
    heldTorch: null,
    torchLit: false,
    autoAttack: loadSettings().autoAttack,
    combatTargetId: null,
    bagCache: null,
    overlay: null,
    wrap: dom.wrap,
    stageHost: dom.stageHost,
    energyBar: dom.energyBar,
    footer: dom.footer,
    hotbar: dom.hotbar,
    clockLabel: null,
    load: async () => undefined,
  };
}

/** Texture for a decor entity: trees by species+variant, small decor by variant. */
export function decorTexture(tex: MapTextures, d: Decor) {
  if (d.kind === 'tree') {
    const t = tex.tree[TREE_KINDS[d.variant >> 1]];
    return (d.variant & 1) ? t.b : t.a;
  }
  // A vein draws its METAL (map/ores.ts). Falls back to the old generic rock so a
  // world snapshot written before ore types existed still renders something.
  if (d.kind === 'ore_rock') return tex.mining[oreNodeSprite(oreByVariant(d.variant))] ?? tex.mining.rock_ore;
  if (tex.terrain[d.kind]) return tex.terrain[d.kind];
  const set = tex.decor[d.kind];
  return set[Math.min(d.variant, set.length - 1)];
}

// True sprite stages for a regrowing tree: sapling → medium tree → big tree
// (small species skip the middle). Real pack sprites, not a scaled-down adult.
export function treeStageTexture(c: FarmCtx, d: Decor, t: number): Texture {
  const kind = TREE_KINDS[d.variant >> 1] ?? 'oak';
  const set = c.tex!.tree[kind];
  const big = kind.endsWith('_big');
  if (big) {
    if (t < 0.35) return set.sapling;
    if (t < 1) {
      const mid = c.tex!.tree[kind.replace('_big', '') as typeof TREE_KINDS[number]];
      return (d.variant & 1) ? mid.b : mid.a;
    }
    return decorTexture(c.tex!, d);
  }
  return t < 0.5 ? set.sapling : decorTexture(c.tex!, d);
}

// Uniform pixel-correct scale; plateaus are full-size composites, the small
// boulder is enlarged so it reads as a rocky outcrop, not a ground rock.
// An ore vein draws at 1.0 — i.e. exactly one tile.
//
// It used to be 1.35, which was right for the old vein art: that sprite sat in a
// corner of its frame, so 1.35 of it still read as a rock. The new pack-sliced
// veins fill their whole 16x16 frame, so the same multiplier drew a 1.35-tile
// boulder — nearly as tall as the hero, for something you chip ore out of. The
// number did not change meaning; the art under it did.
/**
 * Fisherman Fin's on-screen height, measured rather than guessed.
 *
 * OWNER (2026-08-17): "his sprite animation is much bigger than my hero — it
 * should be identical." It was: Fin's art (sprites/decor/oracle_1.png) is 19px
 * tall and was drawn at the shared oracle scale PX*1.7 = 5.1, i.e. ~97 screen px
 * — 2× the hero. The composed hero measures 20 frame-px from hair top (y=21) to
 * sole (y=41) at CHAR_SCALE = 3.2*TILE/64 = 2.4, so he stands exactly one TILE
 * (48px) tall and 13*2.4 ≈ 31px wide. Fin's art is also 13px wide, so matching
 * heights matches widths too.
 *
 * This is Fin only (oracle variant 1). The grove oracle (variant 0) is a 28px
 * robed shrine-keeper nobody complained about and keeps the old scale.
 */
export const HERO_SCREEN_H = TILE;          // 20 frame-px of hero × CHAR_SCALE 2.4
export const FIN_ART_H = 19;                // scripts/build-fisherman-fin.py output
export const FIN_SCALE = HERO_SCREEN_H / FIN_ART_H;

export const decorScale = (d: Decor) => (d.kind === 'oracle' && d.variant === 1 ? FIN_SCALE
  : PX * (d.kind === 'boulder' ? 1.6 : d.kind === 'ore_rock' ? 1.0
  : d.kind === 'volcano_tower' ? 0.46   // 139px native → ~4 tiles tall, imposing but not map-filling
  : d.kind === 'fountain' ? 1.6        // shrine centrepiece, a clear grove landmark
  : d.kind === 'oracle' ? 1.7          // robed quest-giver, a touch larger than a villager
  : 1));

export const isMined = (c: FarmCtx, d: Decor) => d.kind === 'ore_rock' && (c.minedUntil.get(d.id) ?? 0) > Date.now();
// FOLIAGE counts as gathered too. It is deliberately NOT in GATHER_KINDS — that set
// routes the hold-to-gather interaction, and tufts are cleared with a single tap —
// but leaving it out here meant a hydrated foliage harvest could never read as
// spent, so the tuft reappeared however recently it had been pulled.
export const isGathered = (c: FarmCtx, d: Decor) =>
  (GATHER_KINDS.has(d.kind) || FOLIAGE_KINDS.has(d.kind)) && (c.gatheredUntil.get(d.id) ?? 0) > Date.now();
export const stumpFor = (c: FarmCtx, d: Decor) => c.tex!.tree[TREE_KINDS[d.variant >> 1] ?? 'oak'].stump;
export const isFelled = (c: FarmCtx, d: Decor) => d.kind === 'tree' && (c.felledUntil.get(d.id) ?? 0) > Date.now();
export const isCleared = (c: FarmCtx, d: Decor) => isFelled(c, d) && c.clearedStumps.has(d.id);
export const isStump = (c: FarmCtx, d: Decor) => isFelled(c, d) && !c.clearedStumps.has(d.id);   // felled, stump still standing

// Re-apply server-persisted harvest state to the client maps so felled trees / mined
// rocks / gathered props DON'T reappear after a reload. Each row is keyed by tile;
// map it back to the decor id at that tile (matching the resource → decor kind) and
// set the matching regrow timer. Expired rows are pruned server-side before we get here.
// One resource can come from more than one decor kind: 'stone' is paid by both a
// ground rock and a boulder outcrop, and a boulder whose tile only matched 'rock'
// was silently skipped here — so a mined-out boulder came BACK on every reload.
/**
 * Server harvest `kind` -> the client decor kinds it can refer to.
 *
 * grass/flowers were MISSING. The gathering service writes those kinds when the
 * player clears ground cover, so the rows existed and were returned by /field —
 * but nothing here mapped them, so `hydrateHarvests` skipped every one and pulled
 * tufts grew back on every single load. A persistence bug that looked like a
 * regrow-timer bug.
 */
const DECOR_KIND_FOR: Record<string, Decor['kind'][]> = {
  tree: ['tree'], ore_rock: ['ore_rock'], stone: ['rock', 'boulder'], mushroom: ['mushrooms'], bush: ['bush'],
  grass: ['grass_tuft'], flowers: ['flowers'],
};
export function hydrateHarvests(c: FarmCtx) {
  if (!c.world) return;
  // Ground the player has taken, rebuilt from the server snapshot on every load.
  //
  // WORLD GENERATION RUNS FROM THE SEED ALONE and knows nothing about anything the
  // player has built, so it re-scatters trees onto the tile his road or crafting
  // table stands on — the owner's second report. Pruning here, after the farm
  // snapshot has arrived and BEFORE buildScene, means the sprite is never created
  // rather than created and hidden (a hidden sprite still blocks collision, which
  // is how you get an invisible wall on your own road).
  //
  // Buildings are reserved by ANCHOR tile only. The multi-tile kinds (3x2 house,
  // 3x1 hut) have their real footprint in scene.ts's `solidBuildingTiles`, which
  // cannot be imported here — scene.ts imports this module, and closing that loop
  // is a genuine import cycle. `claimedTiles` takes the footprint rule as an
  // argument so whoever owns scene.ts can pass it in without touching this side.
  const reserved = (c.world.reserved ??= new Set<string>());
  for (const t of claimedTiles(c.farm?.buildings ?? [], c.farm?.harvests ?? [])) reserved.add(t);
  // Only BUILT-ON tiles have their decor deleted. A cleared tile keeps its decor
  // object on purpose: that is what the hydration below turns back into a stump —
  // felled but not yet grubbed out, still worth a log to whoever comes back for it.
  // The tile is reserved either way, so nothing new may be placed on it.
  pruneReservedDecor(c.world, reservedTiles(c.farm?.buildings ?? []));

  if (!c.farm?.harvests?.length) return;
  const byTile = new Map<string, Decor[]>();
  for (const d of c.world.decor) {
    const k = `${d.tx},${d.ty}`;
    (byTile.get(k) ?? byTile.set(k, []).get(k)!).push(d);
  }
  for (const h of c.farm.harvests) {
    const want = DECOR_KIND_FOR[h.kind];
    if (!want) continue;
    const d = byTile.get(`${h.tx},${h.ty}`)?.find((e) => want.includes(e.kind));
    if (!d) continue;
    const until = Date.parse(h.regrowUntil);
    if (!Number.isFinite(until)) continue;
    if (h.kind === 'tree') { c.felledUntil.set(d.id, until); if (h.cleared) c.clearedStumps.add(d.id); }
    else if (h.kind === 'ore_rock') c.minedUntil.set(d.id, until);
    else c.gatheredUntil.set(d.id, until);
  }
}

// ─── Plots + buildings (Block 8: farm anywhere) ──────────────────────────────
/** The plot at a tile, if the player has hoed one there. */
export const plotAt = (c: FarmCtx, tx: number, ty: number) =>
  c.farm?.plots.find((p) => p.tx === tx && p.ty === ty);
export const buildingAt = (c: FarmCtx, tx: number, ty: number) =>
  c.farm?.buildings.find((b) => b.tx === tx && b.ty === ty);

/**
 * Re-read the bag and repaint the hotbar.
 *
 * The hotbar's resource counts come from `bagCache`, which was only ever filled
 * ONCE, when the farm loaded. So every action that spends or earns materials —
 * building a fence, chopping a tree, crafting, harvesting — left the old numbers
 * on screen. The server was deducting correctly the whole time; the nav bar simply
 * never asked again, which reads exactly like "my resources are not being spent".
 *
 * Call this after anything that moves a material. Fire-and-forget: a failed
 * refresh must never break the action that just succeeded.
 */
export function refreshBag(c: FarmCtx): void {
  void fieldApi.bag()
    .then((b) => {
      if (!c.alive) return;
      c.bagCache = b;
      bagPainter?.(c);
    })
    .catch(() => undefined);
}

/**
 * Repaint hook, registered by the HUD at load time. This module cannot import
 * `hud.ts` (the HUD imports this one), so the painter registers itself.
 */
let bagPainter: ((c: FarmCtx) => void) | null = null;
export function setBagPainter(fn: (c: FarmCtx) => void): void { bagPainter = fn; }

/**
 * Repaint the hotbar after `c.gearTiers` changes.
 *
 * The bar is painted while the HUD is built, which happens BEFORE the tiers come
 * back from the server — so it drew the placeholder ({sword:1, axe:1, pickaxe:1,
 * hoe:1}, i.e. Wood) and nothing put it right. It only corrected itself when some
 * unrelated click repainted the bar, which is why the tools "become mythril after
 * you click them". Owner reported exactly that.
 *
 * The hero's own look was fixed before this by awaiting the tiers in ensurePlayer,
 * and `syncHeld` fixes the tint of the tool IN HIS HAND — but neither touches the
 * bar's icons or its tier badges, which is the part the player is reading.
 *
 * Goes through the registered painter for the same reason `setBagPainter` does:
 * hud.ts imports this module, so this module cannot import hud.ts.
 */
export function repaintGear(c: FarmCtx): void {
  bagPainter?.(c);
}

/**
 * Placeable buildings — costs mirror the server's BUILDINGS table (for the HUD).
 *
 * `fence` marks a fence entry (grouped into one picker row). `hidden` entries are
 * the old per-shape fence variants: fences AUTO-CONNECT now (see map/fence-connect.ts),
 * so the player places one kind per material and the renderer resolves the corner,
 * tee or end cap. The variants stay defined because farms built before the change
 * still contain them and must keep rendering and reporting a proper name.
 */
/**
 * `buildMin` mirrors the server's BUILDINGS.buildMs in MINUTES (0/absent = instant).
 * The build sheet must state it: committing 30 wood to a Farmhouse without being
 * told it is a one-hour wait is the decision made blind.
 */
export const BUILDINGS: Record<string, { name: string; icon: string; wood: number; stone: number; blurb?: string; fence?: boolean; hidden?: boolean; buildMin?: number; needPop?: number }> = {
  torch: { name: 'Torch', icon: '🔥', wood: 3, stone: 2, blurb: 'burns — lights the ground around it', buildMin: 10 },
  lantern: { name: 'Lantern', icon: '🏮', wood: 2, stone: 3, blurb: 'a steady doorstep light', buildMin: 10 },
  // Fence builder — placeable pieces the player connects by hand.
  fence: { name: 'Wooden fence', icon: '🪵', wood: 1, stone: 0, fence: true, blurb: 'joins up on its own' },
  fence_v: { hidden: true, name: 'Fence │', icon: '🪵', wood: 1, stone: 0, fence: true },
  fence_post: { hidden: true, name: 'Fence post', icon: '🪵', wood: 1, stone: 0, fence: true },
  fence_cnw: { hidden: true, name: 'Fence ┌', icon: '🪵', wood: 1, stone: 0, fence: true },
  fence_cne: { hidden: true, name: 'Fence ┐', icon: '🪵', wood: 1, stone: 0, fence: true },
  fence_csw: { hidden: true, name: 'Fence └', icon: '🪵', wood: 1, stone: 0, fence: true },
  fence_cse: { hidden: true, name: 'Fence ┘', icon: '🪵', wood: 1, stone: 0, fence: true },
  // Stone family — same seven pieces, paid in stone instead of wood.
  fence_stone: { name: 'Stone fence', icon: '🪨', wood: 0, stone: 2, fence: true, blurb: 'joins up on its own' },
  fence_stone_v: { hidden: true, name: 'Stone fence │', icon: '🪨', wood: 0, stone: 2, fence: true },
  fence_stone_post: { hidden: true, name: 'Stone post', icon: '🪨', wood: 0, stone: 2, fence: true },
  fence_stone_cnw: { hidden: true, name: 'Stone fence ┌', icon: '🪨', wood: 0, stone: 2, fence: true },
  fence_stone_cne: { hidden: true, name: 'Stone fence ┐', icon: '🪨', wood: 0, stone: 2, fence: true },
  fence_stone_csw: { hidden: true, name: 'Stone fence └', icon: '🪨', wood: 0, stone: 2, fence: true },
  fence_stone_cse: { hidden: true, name: 'Stone fence ┘', icon: '🪨', wood: 0, stone: 2, fence: true },
  // The hero's OWN home — his bed and his respawn point (server: HOME_KIND).
  // Listed before the House on purpose: it is the first thing the tutorial asks
  // for, and the House beneath it is villager housing, not yours.
  hut: { name: 'Hut', icon: '🛖', wood: 25, stone: 12, blurb: 'your home — sleep & respawn here', buildMin: 15 },
  house: { name: 'House', icon: '🏠', wood: 60, stone: 40, blurb: '+1 villager slot', buildMin: 30 },
  // ── Village (population-gated; the server rejects these below the required
  // population, and the Village panel shows how far off you are).
  // `needPop` mirrors the server's BUILD_UNLOCK_POP (village.config.ts). Without it
  // on the client, a Hamlet building looked exactly as buildable as a torch: you
  // armed it, tapped a green tile, and only the server's 400 explained why nothing
  // happened. It now locks the row and reddens the ghost before the round trip.
  farmhouse: { name: 'Farmhouse', icon: '🏡', wood: 180, stone: 120, blurb: '+2 villager slots', buildMin: 60, needPop: 3 },
  lumber_hut: { name: 'Lumber Hut', icon: '🪵', wood: 110, stone: 45, blurb: 'a Forester works here', buildMin: 40, needPop: 3 },
  mine_shaft: { name: 'Mine Shaft', icon: '⛏️', wood: 60, stone: 140, blurb: 'a Miner works here', buildMin: 45, needPop: 3 },
  forage_hut: { name: 'Forager Hut', icon: '🍄', wood: 90, stone: 60, blurb: 'a Forager works here', buildMin: 35, needPop: 3 },
  // Pens (husbandry). These are the first things the Village and Town tiers ever
  // unlocked — both were label-only before. A pen holds 4 animals; the Barn also
  // doubles as the Herder's workplace, which is why it costs a Town.
  coop: { name: 'Chicken Coop', icon: '🐔', wood: 120, stone: 40, blurb: 'houses 4 chickens', buildMin: 30, needPop: 6 },
  barn: { name: 'Barn', icon: '🐄', wood: 240, stone: 150, blurb: 'houses 4 cows/sheep · a Herder works here', buildMin: 60, needPop: 11 },
  // Crafting stations (A14) — walk up to a placed station and click it to craft.
  workbench: { name: 'Crafting Table', icon: '🔨', wood: 30, stone: 12, blurb: 'craft tools & armor here', buildMin: 15 },
  furnace: { name: 'Furnace', icon: '🔥', wood: 25, stone: 50, blurb: 'needs a Crafting Table', buildMin: 25 },
  forge: { name: 'Forge', icon: '⚒️', wood: 20, stone: 80, blurb: 'needs a Furnace', buildMin: 40 },
  // ── Ground decoration: walkable, instant, its own generous cap (MAX_DECOR).
  road: { name: 'Cobble Road', icon: '🧱', wood: 0, stone: 1, blurb: 'walk on it — lay a run tile by tile' },
  road_sand: { name: 'Sandstone Road', icon: '🟫', wood: 0, stone: 1, blurb: 'warmer paving' },
  path: { name: 'Dirt Path', icon: '🟤', wood: 1, stone: 0, blurb: 'a worn track through the grass' },
  path_pebbles: { name: 'Pebbled Path', icon: '⚪', wood: 1, stone: 0, blurb: 'dirt track, strewn with stones' },
  bridge_wood_h: { name: 'Wooden Bridge ─', icon: '🌉', wood: 8, stone: 2, blurb: 'east–west deck', buildMin: 5 },
  bridge_wood_v: { name: 'Wooden Bridge │', icon: '🌉', wood: 8, stone: 2, blurb: 'north–south deck', buildMin: 5 },
  bridge_stone_h: { name: 'Stone Bridge ─', icon: '🌉', wood: 4, stone: 12, blurb: 'east–west deck', buildMin: 8 },
  bridge_stone_v: { name: 'Stone Bridge │', icon: '🌉', wood: 4, stone: 12, blurb: 'north–south deck', buildMin: 8 },
  // ── Props: purely cosmetic, and SOLID — you walk up to them, not through.
  well: { name: 'Stone Well', icon: '🪣', wood: 2, stone: 10, blurb: 'the heart of a village square', buildMin: 5 },
  fountain: { name: 'Fountain', icon: '⛲', wood: 2, stone: 14, blurb: 'running water', buildMin: 5 },
  barrel: { name: 'Barrel', icon: '🛢️', wood: 3, stone: 0, buildMin: 2 },
  flower_barrel: { name: 'Flower Barrel', icon: '💐', wood: 3, stone: 1, buildMin: 2 },
  signpost: { name: 'Signpost', icon: '🪧', wood: 2, stone: 0, buildMin: 2 },
  bench: { name: 'Wooden Bench', icon: '🪑', wood: 3, stone: 1, buildMin: 2 },
  bench_stone: { name: 'Stone Bench', icon: '🪑', wood: 0, stone: 4, buildMin: 2 },
  hay_bale: { name: 'Hay Bale', icon: '🌾', wood: 2, stone: 0, buildMin: 2 },
  scarecrow: { name: 'Scarecrow', icon: '🎃', wood: 3, stone: 0, buildMin: 2 },
};

/** Building kinds that open the crafting bench when clicked. */
export const STATION_KINDS = new Set(['workbench', 'furnace', 'forge']);

// Held item shown in the hero's hand for the selected hotbar tool (§1).
const HELD_FOR_TOOL: Record<string, string> = { knife: 'sword', hoe: 'hoe', axe: 'axe', pickaxe: 'pickaxe' };
/** Which crafted tier column each held item's colour comes from. */
const TIER_FOR_HELD: Record<string, keyof NonNullable<FarmCtx['gearTiers']>> = {
  sword: 'sword', axe: 'axe', pickaxe: 'pickaxe', hoe: 'hoe',
};
export const syncHeld = (c: FarmCtx) => {
  const item = c.selectedTool ? (HELD_FOR_TOOL[c.selectedTool] ?? null) : null;
  c.player?.setHeld(item);
  // …and colour it by its TIER. The pack draws one sprite per tool and nothing per
  // metal, so a Mythril pickaxe and the wooden one you started with were the same
  // picture in the hand. Tinting toward the ore the tier is forged from is the
  // owner's explicit instruction for exactly this gap (map/ores.ts owns the
  // palette); Wood and Stone tint to 0xffffff, i.e. they stay as drawn.
  const slot = item ? TIER_FOR_HELD[item] : undefined;
  c.player?.setHeldTint(slot ? tierTintNum(c.gearTiers?.[slot] ?? 0) : 0xffffff);
};

/** Light / snuff the carried torch — the visual half only.
 *
 *  One hand, one thing still holds, but emptying it is NOT done here: this module
 *  cannot reach the placement teardown in input.ts (cycle), and writing
 *  `selectedTool` straight from here made a third writer that bypassed it. Raising
 *  the torch goes through `selectTorch` in hud.ts, which puts the tool away by the
 *  same route the hotbar uses. */
export function setTorchLit(c: FarmCtx, on: boolean) {
  c.torchLit = on;
  if (on) syncHeld(c);
  c.heldTorch?.setLit(on);
}

// ─── Tool → animation ────────────────────────────────────────────────────────
// One place that answers "which swing does the hero play?". Every farm action
// routes through `toolAction`, so the animation ALWAYS matches the tool in the
// hotbar: hoe swings the hoe, pickaxe swings the pick, axe swings the axe, knife
// swings the sword. Each of those player rows has its instrument baked in as a
// tool overlay (see ACTIONS in map/player.ts), so the tool is visible in hand for
// the whole swing and disappears again the moment the hero returns to idle/walk.
export const TOOL_ACTION: Record<string, ActionType> = {
  knife: 'attack',
  hoe: 'till',
  axe: 'chop',
  pickaxe: 'mine',
};
/**
 * The action for the currently selected tool.
 *
 * `fallback` is what the target object wants (a tree wants 'chop'), used when no
 * tool is selected — or when the selected tool has no bearing on this object, in
 * which case `strict` callers keep the object's own swing. Bare-hands work
 * ('attack') is the last resort.
 */
export const toolAction = (c: FarmCtx, fallback: ActionType = 'attack'): ActionType =>
  (c.selectedTool ? TOOL_ACTION[c.selectedTool] : undefined) ?? fallback;

/** 4-way facing from the hero toward a world point (dominant axis wins). */
export function dirToPoint(c: FarmCtx, wx: number, wy: number): Dir | undefined {
  if (!c.player) return undefined;
  const dx = wx - c.player.x, dy = wy - (c.player.y - FOOT_OFFSET);
  if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? 'left' : 'right';
  return dy < 0 ? 'up' : 'down';
}
/** 4-way facing from the hero toward the CENTRE of a tile (plots, buildings). */
export const dirToTile = (c: FarmCtx, tx: number, ty: number): Dir | undefined =>
  dirToPoint(c, (tx + 0.5) * TILE, (ty + 0.5) * TILE);

export const cropById = (c: FarmCtx, id: string) => c.crops.find((x) => x.id === id);
export const liveRemaining = (c: FarmCtx, ms: number) => Math.max(0, ms - (Date.now() - c.fetchedAt));

/**
 * The ONLY sanctioned way to store the energy/rest snapshot.
 *
 * It carries a countdown (`nextRefillMs`, ms until the next rest charge) which the
 * HUD interpolates per frame, so the moment it was fetched has to be recorded with
 * it. Assigning `c.energyStatus` directly leaves `energyStatusAt` stale and the
 * timer counts down from the wrong instant — the same trap `setFarm` exists for.
 */
export function setEnergyStatus(c: FarmCtx, st: FarmCtx['energyStatus']): void {
  c.energyStatus = st;
  c.energyStatusAt = Date.now();
}

/**
 * The ONLY sanctioned way to store a farm snapshot.
 *
 * Every timer on screen is interpolated as `remainingMs - (now - fetchedAt)`, so a
 * snapshot whose `fetchedAt` belongs to an OLDER fetch has that whole elapsed
 * session time subtracted from numbers that were measured just now: a freshly
 * placed 40-minute build rendered as "31m" because the last full c.load() was nine
 * minutes ago. Action responses (build/till/water/plant/unbuild/rush) are complete
 * snapshots taken at the moment of the call, so they must stamp the clock too —
 * assigning `c.farm` without it is the bug, and this helper is what makes the two
 * inseparable.
 */
/**
 * The single writer for `c.farm` — and therefore the one place that can see every
 * energy change the server reports, whatever caused it.
 *
 * Energy was previously silent: the bar slid down a little and that was the whole
 * feedback. A player chopping a tree could not tell what the swing had cost, or
 * that it had cost anything, so the bar emptied for no visible reason and running
 * out felt arbitrary. Every action funnels its response through here, so diffing
 * the number here reports the cost of ALL of them — chopping, mining, tilling,
 * watering, gathering — without a call site having to remember to say so.
 *
 * Only DROPS are shown. A refund, a rest, or the extension purchase moves energy
 * up, and "+1" floating over the hero on a heal reads like a reward for the swing
 * that just cost him one.
 */
export function setFarm(c: FarmCtx, farm: Farm): void {
  const before = c.farm?.energy;
  c.farm = farm;
  c.fetchedAt = Date.now();
  if (before === undefined || !c.effects || !c.player) return;
  // Rounded, because energy is fractional server-side (running drains 0.5/tick and
  // foliage costs 0.5): an unrounded diff renders "-0.5000000000000004", and two
  // half-costs in a row would otherwise show nothing at all.
  const spent = Math.round((before - farm.energy) * 10) / 10;
  if (spent > 0) c.effects.cost(c.player.x, c.player.y - FOOT_OFFSET, spent);
}
export function cropStage(c: FarmCtx, cropId: string, remainingMs: number, watered: boolean, ready: boolean): number {
  if (ready) return 3;
  if (!watered) return 0;                 // seeds wait for water — no growth until watered
  const crop = cropById(c, cropId);
  if (!crop) return 0;
  return Math.min(3, Math.max(0, Math.floor((1 - remainingMs / crop.growthMs) * 4)));
}

export const clampPlayer = (c: FarmCtx, x: number, y: number): [number, number] => [
  Math.max(TILE * 0.5, Math.min((c.world!.cols - 0.5) * TILE, x)),
  Math.max(TILE, Math.min((c.world!.rows - 0.3) * TILE, y)),
];

// Resources burst out of a chopped tree / cracked rock as their ICON and arc
// down (auto-collected — no tapping needed).
// Item icons are loaded on demand: animal loot is a long tail (three wools, three
// meats, feather, leather) and most runs touch only a couple of them.
/** Where an item's art lives. Split by WHERE IT WAS SLICED FROM, not by use. */
export function itemTextureUrl(itemId: string): string {
  const oreDrop = /^(ore|bar)_(copper|iron|gold|mythril)$/.exec(itemId);
  if (oreDrop) return `/sprites/mining/${oreDrop[1] === 'ore' ? 'chunk' : 'bar'}_${oreDrop[2]}.png`;
  const dir = RESOURCE_ITEMS.has(itemId) ? 'resources'
    : itemId.startsWith('produce_') ? 'crops'
    : 'items';
  return `/sprites/${dir}/${itemId}.png`;
}

/**
 * Item icon, FETCHED — not merely named.
 *
 * OWNER BUG (2026-08-14): "falling bones and meat from orc doesn't appear like
 * trees from trees". Wood, ore, gem and stone come from the preloaded `tex.res`
 * bucket and draw fine; everything else went through `Texture.from(url)`, which
 * in Pixi 8 does NOT fetch an unloaded URL — it returns Texture.EMPTY. So every
 * mob and animal drop (bones, meat, feather, leather, wool) spawned an invisible
 * sprite that arced to the ground and was collected, showing nothing.
 *
 * Assets.load actually fetches. It is async, so callers that need the art on a
 * sprite use `applyItemTexture`, which swaps the texture in when it lands — the
 * same pattern loadBuildingTex already uses for building art.
 */
export const itemTexture = (c: FarmCtx, itemId: string): Texture => {
  let t = c.itemTex.get(itemId);
  if (!t) {
    const url = itemTextureUrl(itemId);
    const cached = Assets.cache.has(url) ? (Assets.get(url) as Texture | undefined) : undefined;
    if (cached) { c.itemTex.set(itemId, cached); return cached; }
    // Not loaded yet: fetch it, and let applyItemTexture put it on the sprite.
    void Assets.load(url).then((tex: Texture) => { c.itemTex.set(itemId, tex); }).catch(() => { /* missing art: stays blank */ });
    return Texture.EMPTY;
  }
  return t;
};

/** Put an item's icon on a sprite, now or as soon as it has loaded. */
export function applyItemTexture(c: FarmCtx, s: { texture: Texture; destroyed?: boolean }, itemId: string): void {
  const t = itemTexture(c, itemId);
  if (t !== Texture.EMPTY) { s.texture = t; return; }
  void Assets.load(itemTextureUrl(itemId))
    .then((tex: Texture) => { if (!s.destroyed) s.texture = tex; })
    .catch(() => { /* missing art: the sprite stays blank rather than throwing */ });
}

const RESOURCE_ITEMS = new Set(['wood', 'ore', 'gem', 'stone']);

// Every mutation goes through the funnel: it owns single-flight, the debounce
// and the actionId, so no call site has to remember a `busy` flag again.
// `key` separates targets of one type (plot 3's harvest is not plot 4's).
export async function guard(type: string, fn: (actionId: string) => Promise<void>, key?: string) {
  try {
    await dispatchAction(type, fn, { key });
  } catch (e) {
    if (isDropped(e)) return;   // the funnel refused to send — not a failure, say nothing
    toast(actionErrorMessage(e), 'error');
  }
}

/**
 * What to SHOW the player when an action is refused.
 *
 * A silent no-op is the worst outcome: the building doesn't appear, nothing is
 * said, and it reads as a broken game. Two flavours of 429 reach us and only one
 * is written for humans:
 *   • the placement limiter answers with a full sentence naming the budget and the
 *     seconds left ("Slow down — you have placed or demolished 12 structures…") —
 *     show it verbatim;
 *   • the generic HTTP throttler answers "ThrottlerException: Too Many Requests",
 *     which means nothing to a player — replace it.
 */
export function actionErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Action failed';
  // A 401 here is TERMINAL: the network layer already retried once behind a silent
  // refresh, so reaching this point means the refresh failed too and the player is
  // signed out. Handled here because this is the one funnel the game uses to turn an
  // API error into words — before, every caller happily printed the raw
  // "Unauthorized" and the game carried on pretending it was logged in, which is
  // what made a lapsed session look like a broken Rest button.
  if (handleSessionExpiry(e)) return 'Your session expired — signing you out';
  if (e.status === 429 && /throttlerexception/i.test(e.message)) return 'Slow down a moment — too many actions at once';
  return e.message || 'Action failed';
}

/**
 * Mount a farm panel — the ONLY sanctioned way, because it is what guarantees the
 * panel can be dismissed.
 *
 * Every farm panel (build, bag, crafting, village, forager, planter) lives in
 * `c.overlay` and used to be appended by hand. Two of them had no exit at all in
 * immersive mode: the docked build sheet's `×` sits in the top-right corner, which
 * is exactly where the immersive floating cluster (chat/settings/UI, z-56) lands
 * once the header is hidden — so every click on the close button hit a button
 * behind it, and there was no Escape handler and no backdrop to fall back on.
 *
 * Opening through here attaches Escape to whatever is on screen, so the keyboard is
 * always a way out regardless of which corner the chrome is occupying. The listener
 * is capturing and stops propagation so the farm view's own key handling (and any
 * nested confirm) can't act on the same press.
 */
export function openOverlay(c: FarmCtx, node: HTMLElement): void {
  closePlanter(c);
  c.overlay = node;
  document.body.appendChild(node);
  overlayEsc = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || c.overlay !== node) return;
    e.stopPropagation();
    closePlanter(c);
  };
  window.addEventListener('keydown', overlayEsc, true);
}
let overlayEsc: ((e: KeyboardEvent) => void) | null = null;

export function closePlanter(c: FarmCtx) {
  if (c.overlay) playSfx('ui_close');
  c.overlay?.remove();
  c.overlay = null;
  if (overlayEsc) { window.removeEventListener('keydown', overlayEsc, true); overlayEsc = null; }
}

// Load the hand-authored map draft once; classified into terrain by the world gen.
export async function loadDraft(c: FarmCtx) {
  if (c.draftData !== undefined) return c.draftData;
  try {
    const img = new Image();
    img.src = '/map-draft.webp';   // lossless webp — exact pixels, ~45% smaller than the png
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const g = canvas.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(img, 0, 0);
    c.draftData = { data: g.getImageData(0, 0, canvas.width, canvas.height).data, w: canvas.width, h: canvas.height };
  } catch { c.draftData = null; }
  return c.draftData;
}
